import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable, throwError, of, BehaviorSubject } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';


export interface CustomerDetails {
  customerId: string;
  name: string;
  email: string;
  phone: string;
}

export interface CreateOrderPayload {
  amount: number;
  currency?: string;
  description?: string;
  customer: CustomerDetails;
  metadata?: Record<string, any>;
}

export interface OrderResponse {
  orderId: string;
  paymentSessionId: string;
  amount: number;
  currency: string;
  status: string;
  expiresAt: string;
}

export interface PaymentVerification {
  orderId: string;
  amount: number;
  currency: string;
  status: string;
  paymentMethod?: string;
  paidAt?: string;
  invoiceNo?: string;
}

export interface RefundPayload {
  orderId: string;
  refundAmount: number;
  refundNote?: string;
}

export interface PaymentListResponse {
  data: PaymentRecord[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaymentRecord {
  id: string;
  orderId: string;
  cfOrderId: string;
  amount: number;
  currency: string;
  status: string;
  customerName: string;
  customerEmail: string;
  paymentMethod?: string;
  description?: string;
  paidAt?: string;
  createdAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class PaymentService {

  private loggedIn$ = new BehaviorSubject<boolean>(false);
  private readonly baseUrl = `${environment.api_URL}/cashfree-pg`;
  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.loggedIn$.next(this.hasUser());
    }
  }

  private hasUser(): boolean {
    return !!localStorage.getItem('Cirlced_User');
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  subscriptionPymnt(data: any): Observable<any> {
    return this.http.post(`${environment.api_URL}/payment`, data)
      .pipe(
        retry(1),
        catchError((error: HttpErrorResponse) => {
          if (!this.isBrowser()) {
            return of({ status: false, message: 'SSR mode' });
          }
          return throwError(() => ({
            status: false,
            message: this.getErrorMessage(error),
            error,
          }));
        })
      );
  }
  verifyPayment(id: any,isOutsideBangalore:boolean): Observable<any> {
    return this.http.get(`${environment.api_URL}/payment/status/${id}?isOutsideBangalore=${isOutsideBangalore}`)
      .pipe(
        retry(1),
        catchError((error: HttpErrorResponse) => {
          if (!this.isBrowser()) {
            return of({ status: false, message: 'SSR mode' });
          }
          return throwError(() => ({
            status: false,
            message: this.getErrorMessage(error),
            error,
          }));
        })
      );
  }

  downloadReceipt(invoiceId: any): Observable<any> {
    return this.http.get(`${environment.api_URL}/payment/${invoiceId}/download`,{
      responseType: 'blob'
    })
      .pipe(
        retry(1),
        catchError((error: HttpErrorResponse) => {
          if (!this.isBrowser()) {
            return of({ status: false, message: 'SSR mode' });
          }
          return throwError(() => ({
            status: false,
            message: this.getErrorMessage(error),
            error,
          }));
        })
      );
  }
  downloadInvoice(invoiceNo: string): void {
    this.http.get(`${environment.api_URL}/invoice/${invoiceNo}/download`, { responseType: 'blob' })
      .subscribe(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${invoiceNo}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      });
  }

  getTransaction(): Observable<any> {
    return this.http.get(`${environment.api_URL}/invoice/my`)
      .pipe(
        retry(1),
        catchError((error: HttpErrorResponse) => {
          if (!this.isBrowser()) {
            return of({ status: false, message: 'SSR mode' });
          }
          return throwError(() => ({
            status: false,
            message: this.getErrorMessage(error),
            error,
          }));
        })
      );
  }
  editUser(id:any,data:any): Observable<any> {
    console.log(id,data)
    return this.http.patch(`${environment.api_URL}/auth/edit/${id}`,{name:data.name,email:data.email})
      .pipe(
        retry(1),
        catchError((error: HttpErrorResponse) => {
          if (!this.isBrowser()) {
            return of({ status: false, message: 'SSR mode' });
          }
          return throwError(() => ({
            status: false,
            message: this.getErrorMessage(error),
            error,
          }));
        })
      );
  }

  createOrder(payload: CreateOrderPayload): Observable<OrderResponse> {
    return this.http.post<OrderResponse>(`${this.baseUrl}/orders`, payload);
  }

  verifyCPGPayment(orderId: string): Observable<PaymentVerification> {
    return this.http.get<PaymentVerification>(`${this.baseUrl}/verify/${orderId}`);
  }

  getOrder(orderId: string): Observable<PaymentRecord> {
    return this.http.get<PaymentRecord>(`${this.baseUrl}/orders/${orderId}`);
  }

  listPayments(page = 1, limit = 20): Observable<PaymentListResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    return this.http.get<PaymentListResponse>(this.baseUrl, { params });
  }

  initiateRefund(payload: RefundPayload): Observable<{ refundId: string; status: string }> {
    return this.http.post<{ refundId: string; status: string }>(
      `${this.baseUrl}/refunds`,
      payload,
    );
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    if (error?.error?.message) return error.error.message;
    switch (error.status) {
      case 0: return 'Network error. Please check your connection.';
      case 400: return 'Bad request.';
      case 401: return 'Unauthorized.';
      case 403: return 'Access denied.';
      case 404: return 'Not found.';
      case 500: return 'Server error.';
      default: return 'Something went wrong.';
    }
  }
}